import { Logger } from '@/shared/services/logger.service';

type SoundMap = Record<string, HTMLAudioElement>;

interface SoundDefinition {
  label: string;
  value: string;
  filename: string;
}

export class AudioNotificationService {
  private static instance: AudioNotificationService;
  private sounds: SoundMap = {};
  private isMuted = false;
  private volume = 1.0;
  // Chrome/Edge block audio.play() with sound until the page has had a user gesture
  // (or a high per-origin Media Engagement Index), so a notification can arrive with
  // no prior interaction and play() silently rejects. Queue it and retry on the
  // user's next interaction — same fix already used for meeting audio, see
  // GlobalMeetingAudio.tsx (NGE-681).
  private pendingRetries: { name: string; volume: number; queuedAt: number }[] = [];
  private autoplayUnlockBound = false;

  /** Beyond this, a queued sound is dropped rather than replayed without context. */
  private static readonly RETRY_MAX_AGE_MS = 60_000;

  private soundDefinitions: SoundDefinition[] = [
    { label: 'Chat Message', value: 'chat-notification', filename: 'notification-sound.mp3' },
    { label: 'Blow', value: 'blow', filename: 'blow.mp3' },
    { label: 'Bubble', value: 'bubble', filename: 'bubble.mp3' },
    { label: 'Guest Visit Notification', value: 'channel', filename: 'channel-alert.mp3' },
    { label: 'Doorbell Chime', value: 'ding-dong', filename: 'ding-dong.mp3' },
    { label: 'Office Entry Knock', value: 'knock', filename: 'knock.mp3' },
    { label: 'Locked Door Knock', value: 'lock-knock', filename: 'short-knock.mp3' },
    { label: 'Door Lock', value: 'lock', filename: 'lock.mp3' },
    { label: 'Door Unlock', value: 'unlock', filename: 'unlock.mp3' },
  ];

  private constructor() {
    this.preloadSounds();
  }

  static getInstance(): AudioNotificationService {
    if (!AudioNotificationService.instance) {
      AudioNotificationService.instance = new AudioNotificationService();
    }
    return AudioNotificationService.instance;
  }

  private preloadSounds() {
    this.soundDefinitions.forEach(({ value, filename }) => {
      const audio = new Audio(`/audio/${filename}`);
      audio.volume = this.volume;
      audio.load();
      this.sounds[value] = audio;
    });
  }

  getAvailableSounds(): { label: string; value: string }[] {
    return this.soundDefinitions.map(({ label, value }) => ({ label, value }));
  }

  play(name: string, volumeOverride?: number) {
    Logger.debug(
      `[AudioNotificationService] play("${name}") — muted=${this.isMuted} ua=${navigator.userAgent}`
    );
    if (this.isMuted) return;
    const audio = this.sounds[name];
    if (!audio) {
      Logger.warn(`[AudioNotificationService] Sound not found: "${name}"`);
      return;
    }
    Logger.debug(
      `[AudioNotificationService] "${name}" readyState=${audio.readyState} networkState=${audio.networkState} src=${audio.currentSrc || audio.src}`
    );
    const volume = volumeOverride ?? this.volume;
    this.playClone(name, audio, volume).catch(() => {
      this.queueRetryOnNextInteraction(name, volume);
    });
  }

  private playClone(name: string, audio: HTMLAudioElement, volume: number): Promise<void> {
    const instance = audio.cloneNode(true) as HTMLAudioElement;
    instance.volume = volume;
    return instance
      .play()
      .then(() => {
        Logger.debug(
          `[AudioNotificationService] "${name}" play() resolved — sound should be audible now`
        );
      })
      .catch((err) => {
        Logger.warn(
          `[AudioNotificationService] Playback failed: "${name}" — name=${err?.name} message=${err?.message}`,
          err
        );
        throw err;
      });
  }

  private queueRetryOnNextInteraction(name: string, volume: number) {
    // Only the most recent blocked sound is kept. The queue used to be unbounded and
    // replayed in full, so a long unattended session greeted the user's next click with a
    // rapid volley of every sound they had missed — and a single chime already conveys
    // "something happened while you were away".
    this.pendingRetries = [{ name, volume, queuedAt: Date.now() }];
    Logger.debug(`[AudioNotificationService] queued "${name}" for retry on next interaction`);

    if (this.autoplayUnlockBound) return;
    this.autoplayUnlockBound = true;

    const resume = () => {
      this.autoplayUnlockBound = false;
      document.removeEventListener('pointerdown', resume);
      document.removeEventListener('keydown', resume);
      document.removeEventListener('touchstart', resume);

      const pending = this.pendingRetries;
      this.pendingRetries = [];

      pending.forEach(({ name: retryName, volume: retryVolume, queuedAt }) => {
        // Replaying a chime for something that happened long ago is more confusing than
        // silence, since there is no longer any on-screen context for it.
        const age = Date.now() - queuedAt;
        if (age > AudioNotificationService.RETRY_MAX_AGE_MS) {
          Logger.debug(`[AudioNotificationService] dropping stale "${retryName}" (${age}ms old)`);
          return;
        }
        const retryAudio = this.sounds[retryName];
        if (retryAudio) this.playClone(retryName, retryAudio, retryVolume).catch(() => {});
      });
    };
    document.addEventListener('pointerdown', resume, { once: true });
    document.addEventListener('keydown', resume, { once: true });
    document.addEventListener('touchstart', resume, { once: true });
  }

  mute() {
    this.isMuted = true;
  }

  unmute() {
    this.isMuted = false;
  }

  toggleMute() {
    this.isMuted = !this.isMuted;
  }

  isMutedStatus() {
    return this.isMuted;
  }

  setVolume(percent: number) {
    this.volume = Math.max(0, Math.min(100, percent)) / 100;
  }

  getVolumePercent() {
    return Math.round(this.volume * 100);
  }
}

export const GlobalAudioNotificationService = AudioNotificationService.getInstance();
